import React, { useRef } from 'react';

// Components
import Editor from '@monaco-editor/react';
  
const SampleSolutionBox = () =>  {

    // Monaco editor ref, for dynamic height
    const editorRef = useRef(null);

    // Monaco Editor custom theme
    const handleEditorWillMount = (monaco) => {
        monaco.editor.defineTheme('transparent-dark', {
          base: 'vs-dark',
          inherit: true,
          rules: [
            {
              token: 'comment',
              foreground: '#4e5b75', // navy blue
            },
          ],
          colors: {
            'editor.background': '#272928e6', // transparent black
            'editorLineNumber.foreground': '#555',
            'editorCursor.foreground': '#fff',
            'editorLineHighlightBackground': '#ffffff10',
          },
        });
    };

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
    
        // Resize height based on content
        const updateHeight = () => {
            const lineHeight = editor.getOption(monaco.editor.EditorOption.lineHeight);
            const lineCount = editor.getModel()?.getLineCount() || 1;
            const contentHeight = lineCount * lineHeight;
        
            editor.layout({
              width: editor.getLayoutInfo().width,
              height: contentHeight,
            });
        };
    
        updateHeight();
    
        editor.onDidChangeModelContent(() => {
          updateHeight();
        });
    };
    
	return (
        <div style={{padding: '.75rem 1rem', background: 'rgb(0,0,0)', border: '1px solid rgb(39,39,42)', borderRadius: '.75rem'}}>
            <h2 style={{color: 'rgb(228,228,231)', fontWeight: '600', fontSize: '14px', margin: 0}}>
                Problem Statement
            </h2>
            <p style={{color: 'rgb(212,212,216)', fontSize: '13px', lineHeight: '1.25rem', marginTop: '.5rem'}}>
                Given an array of integers and a target value, find two numbers in the array that add up to the target and return their indices.
            </p>
            {/* Thoughts section */}
            <div style={{margin: '1rem 0'}}>
                <h2 style={{color: 'rgb(228,228,231)', fontWeight: '600', fontSize: '14px', margin: 0}}>
                    Thoughts (Read these aloud)
                </h2>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.75rem'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>
                        I need a way to quickly look up if the complement of a number (target - current number) exists in the array.
                    </p>
                </div>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.75rem'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>   
                        Using a hash table would allow constant time look-up for complements, which can make this process efficient.
                    </p>
                </div>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', margin: '.75rem 0'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>
                        So, I'll iterate through the numbers and for each number, check if its complement is present in the hash table.
                    </p>
                </div>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.25rem'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>
                        If the complement is found in the hash table, I'll return the indices of the current number and its complement.
                    </p>
                </div>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.25rem'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>
                        And if the complement isn't found, I'll add the current number and its index to the hash table for future reference.
                    </p>
                </div>
            </div>
            {/* Code solution */}
            <div style={{margin: '1rem 0'}}>
                <h2 style={{color: 'rgb(228,228,231)', fontWeight: '600', fontSize: '14px', margin: 0}}>
                    Solution:
                </h2>
                <div style={{ padding: '1rem 0', marginTop: '.75rem', borderRadius: '5px', width: '100%', maxWidth: '100%', overflowX: 'auto', background: '#272928e6'}}>
                    <Editor 
                        theme='transparent-dark' 
                        defaultLanguage="python" 
                        defaultValue={`
class Solution:  # Define the class Solution
    def twoSum(self, nums: List[int], target: int) -> List[int]:  # Define the method twoSum
        lookup = {}  # Create a dictionary to store numbers and their indices
        for i, num in enumerate(nums):  # Iterate through the array
            complement = target - num  # Calculate complement
            if complement in lookup:  # Check if complement is already in lookup
                return [lookup[complement], i]  # Return indices of the complement and current number
            lookup[num] = i  # Store the index of the current number in the lookup table
                        `}
                        beforeMount={handleEditorWillMount}
                        onMount={handleEditorDidMount}
                        options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            scrollBeyondLastColumn: 5, // Allow horizontal scrolling
                            overviewRulerLanes: 0,
                            scrollbar: {
                            vertical: 'hidden',
                            handleMouseWheel: false,
                            },
                            lineNumbers: 'on',
                            domReadOnly: true,
                            readOnly: true
                        }}
                    />
                </div>
            </div>
            {/* Complexity section */}
            <div style={{margin: '1rem 0'}}>
                <h2 style={{color: 'rgb(228,228,231)', fontWeight: '600', fontSize: '14px', margin: 0}}>
                    Complexity
                </h2>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.75rem'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>
                        Time: O(n)
                    </p>
                </div>
                {/* Bulleted List Item */}
                <div style={{display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '.75rem'}}>
                    {/* Bullet point */}
                    <div style={{background: '#e5e7eb', borderRadius: '50%', width: '.375rem', height: '.375rem', margin: '8px 0 auto'}}></div>
                    {/* Text */}
                    <p style={{color: 'rgb(212,212,216)', fontSize: '.875rem', lineHeight: '1.25rem', margin: '0'}}>
                        Space: O(n)
                    </p>
                </div>
            </div>
    </div>
	);
}
  
export default SampleSolutionBox;
  